import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Mail, ArrowUpRight } from "lucide-react";
import "./AboutUs.css";

// Animation configs
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

function AboutUs() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      question: "Is this a complete picture of heat stress in Indian garment supply chains?",
      answer: "No. This dashboard is a preliminary and evolving attempt to better understand how rising temperatures may affect garment workers across India.\n\nHeat stress inside factories is shaped by many factors beyond outdoor temperature alone, including ventilation, building design, roofing materials, humidity, crowding, access to drinking water and toilets, production targets, work intensity, rest breaks, and health conditions.\n\nThis platform should therefore be understood as a starting point for further research, collaboration, and worker-centred interventions rather than a definitive measurement of workplace heat exposure."
    },
    {
      question: "What data does the dashboard currently use?",
      answer: "The platform currently combines:\n• Factory-level production location data from Open Supply Hub\n• Temperature data from the India Meteorological Department (IMD)\n• Humidity data sourced through Open-Meteo\n\nTogether, these datasets help identify regions and industrial clusters where heat exposure risks may require further attention and investigation.\n\nOpen Supply Hub’s data also helps identify other actors connected to production locations, including brands, certification initiatives, audit firms, and other supply chain stakeholders. This creates opportunities for different actors connected to the same factory or industrial cluster to collaborate, respond, and strengthen solutions around worker safety and climate adaptation."
    },
    {
      question: "Does this dashboard measure indoor factory temperatures?",
      answer: "No. The current platform does not directly measure indoor factory temperatures or real-time workplace conditions.\n\nAt present, the dashboard uses publicly-available climate and geographic datasets to begin mapping broader heat exposure patterns across garment production regions.\n\nWe recognise that actual workplace conditions inside factories may differ significantly depending on ventilation, infrastructure, machinery, factory design, crowding, and work processes.\n\nHowever, when outdoor temperatures and humidity levels are already reaching dangerous levels across industrial regions, it raises urgent concerns about what workers may be experiencing inside enclosed factory environments during long production shifts."
    },
    {
      question: "Why create this dashboard if the data is incomplete?",
      answer: "One of the biggest challenges around heat stress in garment supply chains is the absence of accessible public infrastructure connecting climate realities to labour conditions and production facilities.\n\nToo often, worker testimonies about heat remain isolated stories rather than becoming part of larger conversations around supply chains, occupational safety, climate adaptation, and business responsibility. While worker interviews and qualitative research remain critical, there is also a need for broader quantitative and geographic data to help understand the possible scale and distribution of heat exposure across garment production regions.\n\nAt the same time, sustainability and decarbonisation discussions frequently move forward without enough attention to how rising heat is already affecting workers on production floors today. While more research and deeper evidence-building are still needed, we believe urgent action cannot wait for perfect research studies before meaningful interventions begin.\n\nEven preliminary public-interest tools can help identify patterns, support collaboration, strengthen worker protections, and push institutions to take the issue more seriously. If publicly available temperature and humidity data already indicate severe heat conditions across industrial regions, that alone should raise important concerns about what workers may be experiencing inside factories under intense production pressure.\n\nThis platform is an attempt to make heat stress more visible within garment supply chains and support more informed, collaborative, and worker-centred responses."
    },
    {
      question: "Is this a funded project?",
      answer: "No. This dashboard was developed as a fully pro bono and collaborative public-interest initiative involving HeatWatch, Open Supply Hub, engineers and garment workers. The project emerged from shared concerns around the growing impact of heat stress on garment workers and the lack of publicly accessible infrastructure to understand the issue systematically."
    },
    {
      question: "What kinds of solutions are needed to address heat stress in garment factories?",
      answer: "There is no single solution to workplace heat stress.\n\nSome interventions may be relatively basic but critically important such as access to clean drinking water, functioning toilets, ventilation, cooling systems, shaded rest areas, medical support, paid rest breaks, and heat-sensitive production planning.\n\nOther interventions require broader structural changes involving factory infrastructure, building design, climate adaptation planning, occupational safety regulations, purchasing practices, and supply chain accountability.\n\nMany suppliers may require significant financial support and long-term investment to implement meaningful heat adaptation measures. Fashion brands, who often capture a substantial share of profits within global garment supply chains, therefore have a responsibility to support suppliers through fairer pricing structures, longer sourcing commitments, realistic production timelines, and responsible purchasing practices that make worker protection measures financially possible.\n\nGovernments also play a critical role in strengthening occupational safety standards, labour inspections, public health systems, and heat-related worker protections.\n\nHeat stress cannot be separated from broader labour conditions within garment supply chains, including low wages, excessive overtime, production pressure, precarious employment, and limited access to trade unions and collective bargaining in many contexts. Workers who are already economically vulnerable often have the least ability to slow down, take breaks, access healthcare, or refuse unsafe working conditions during extreme heat.\n\nMost importantly, workers and their trade unions must be central to identifying risks, shaping solutions, and determining what meaningful workplace protections should look like in practice."
    },
    {
      question: "Will the dashboard expand in the future?",
      answer: "We hope so.\n\nThis is an evolving public-interest project, and we recognise there are many important areas that could be strengthened over time.\n\nWe also hope the platform can encourage broader collaboration between workers, trade unions, researchers, suppliers, brands, public health experts, and civil society organisations working on climate and labour issues.\n\nIf you are interested in collaborating, contributing ideas, sharing research, highlighting interventions, or helping strengthen the platform, we would be very happy to hear from you."
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="about-container">
      <div className="about-paper">
        {/* Header Section */}
        <motion.header
          className="about-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h1>About the Project</h1>
          <div className="header-intro-grid">
            <p className="tagline">
              Over the past few years, rising temperatures and increasingly intense summer heat have begun reshaping working conditions across India. While much public attention around heat stress has focused on outdoor workers, conversations with garment workers in Gujarat, the National Capital Region (NCR), and Tamil Nadu reveals that extreme heat is becoming a growing concern inside textile and garment factories.
            </p>
            <div className="header-intro-subtext">
              <p>
                Workers, especially women workers, report worsening health conditions linked to heat exposure inside factories with poor ventilation, non-functioning fans, overcrowded production lines, and constant pressure to maintain production targets despite rising temperatures.
              </p>
              <p>
                Many described dizziness, dehydration, headaches, fainting, skin problems, irregular menstruation, worsening chronic health conditions, and the difficult trade-offs they are forced to make between rest, hydration, wages, and production pressure.
              </p>
            </div>
          </div>
        </motion.header>

        {/* Narrative Sections */}
        <motion.div
          className="about-sections"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section 1: From Worker Testimonies to Systemic Questions */}
          <motion.div className="about-section" variants={itemVariants}>
            <h2>From Worker Testimonies to Systemic Questions</h2>
            <p>
              HeatWatch first began documenting some of these experiences through worker interviews and reporting, including articles published in{' '}
              <a href="https://www.thehindu.com/news/national/tamil-nadu/inside-tamil-nadus-textile-factories-where-women-reel-under-harsh-summer-heat/article69609882.ece" target="_blank" rel="noopener noreferrer" className="about-link"><em>The Hindu</em></a>{' '}
              and the{' '}
              <a href="https://www.business-humanrights.org/en/blog/heat-stress-health-and-the-hidden-cost-of-fashion-we-are-not-asking-for-luxury-just-cold-water/" target="_blank" rel="noopener noreferrer" className="about-link"><em>Business &amp; Human Rights Resource Centre</em></a>.
              {' '}But these worker testimonies also raised larger questions:
            </p>
            <div className="questions-callout">
              <ul>
                <li>How widespread is heat stress across Indian garment supply chains?</li>
                <li>How many workers may be affected?</li>
                <li>What kinds of factories and industrial clusters are most exposed?</li>
                <li>And why does so little public infrastructure exist to understand these risks systematically?</li>
              </ul>
            </div>
            <p>
              In response, HeatWatch, researchers from the Tata Institute of Social Sciences (TISS), and garment workers, collaborated on further worker documentation and research, including the report{' '}
              <a href="https://drive.google.com/file/d/1ExF2jDtWGADK5cegL5OwTMwU-_fbLVaa/view" target="_blank" rel="noopener noreferrer" className="about-link"><em>Breaking Point: Heat and the Garment Floor</em></a>.
            </p>
            <p>
              Yet even as worker testimonies and qualitative research highlighted the severity of the issue, significant gaps remained in publicly accessible quantitative and geographic data linking heat conditions to garment production locations.
            </p>
            <p style={{ fontWeight: 500 }} className="highlight-text">
              This dashboard is a first attempt to help bridge that gap.
            </p>
          </motion.div>

          {/* Section 2: Building the Dashboard */}
          <motion.div className="about-section" variants={itemVariants}>
            <h2>Building the Dashboard</h2>
            <p>
              Developed through a collaborative effort between HeatWatch, Open Supply Hub, software engineers, researchers, and garment workers, the platform combines factory-level production location data from Open Supply Hub with temperature data from the India Meteorological Department and humidity data sourced through Open-Meteo.
            </p>
            <p>
              Together, these datasets help map how rising temperatures intersect with industrial clusters and worker populations across India’s garment sector.
            </p>
            <p>
              Building the platform required not only research and worker engagement, but also substantial technical collaboration.
            </p>
            <div className="acknowledgement-card">
              <p>
                We are deeply grateful to <strong>Manvika Athwani</strong> who developed the data pipeline and database architecture underpinning the platform, and <strong>Aakash Khatter</strong> who built the dashboard interface and led its front-end integration.
              </p>
            </div>
          </motion.div>

          {/* Section 3: Why This Matters */}
          <motion.div className="about-section" variants={itemVariants}>
            <h2>Why This Matters</h2>
            <p>
              At its core, this dashboard is an evolving public-interest experiment — an attempt to bring together worker testimonies, climate data, and supply chain knowledge to support better conversations around labour rights, climate adaptation, ethical sourcing, and business responsibility within global garment supply chains.
            </p>
            <p>
              Rather than treating ‘climate’ and ‘labour’ as separate conversations, the platform seeks to understand how rising heat is already reshaping everyday working conditions and production realities within factories and global supply chains.
            </p>
          </motion.div>

          {/* Section 4: Who This Resource Is For */}
          <motion.div className="about-section" variants={itemVariants}>
            <h2>Who This Resource Is For</h2>
            <p>
              This resource is intended for workers, trade unions, manufacturers, fashion brands, researchers, policymakers, and journalists seeking to better understand how heat stress is affecting Indian textile and garment supply chains. It is also intended to strengthen collaboration around meaningful solutions by bringing together diverse expertise, experiences, and approaches from across sectors and disciplines.
            </p>
          </motion.div>
        </motion.div>

        {/* FAQ Section */}
        <motion.section
          className="faq-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="faq-header">
            <h2>Frequently Asked Questions (FAQ)</h2>
            <p>Learn more about our methodology, goals, and the challenges of mapping heat stress.</p>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${expandedFaq === index ? "active" : ""}`}
              >
                <button
                  className="faq-question-btn"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={expandedFaq === index}
                >
                  <span>{faq.question}</span>
                  <ChevronDown className="faq-icon" />
                </button>
                <AnimatePresence initial={false}>
                  {expandedFaq === index && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="faq-answer-wrapper"
                    >
                      <div className="faq-answer-content">
                        {faq.answer.split("\n\n").map((paragraph, pIdx) => {
                          if (paragraph.startsWith("The platform currently combines:")) {
                            return (
                              <div key={pIdx}>
                                <p>{paragraph.split("\n")[0]}</p>
                                <ul>
                                  {paragraph.split("\n").slice(1).map((li, liIdx) => (
                                    <li key={liIdx}>{li.replace("• ", "")}</li>
                                  ))}
                                </ul>
                              </div>
                            );
                          }
                          return <p key={pIdx}>{paragraph}</p>;
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Project Partners & Contact Section */}
        <motion.section
          className="partners-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Project Partners</h2>
          <div className="partners-grid">
            <div className="partner-card">
              <h3>About HeatWatch</h3>
              <p>
                <a href="http://www.heatwatch.in/" target="_blank" rel="noopener noreferrer" className="about-link">HeatWatch</a> is a nonprofit working at the intersection of heat, public health, labour rights, and climate justice in India. Its work focuses on documenting the impacts of extreme heat on workers and vulnerable communities, supporting grassroots interventions, conducting participatory research, and strengthening public conversations around climate adaptation and worker safety.
              </p>
            </div>
            <div className="partner-card">
              <h3>About Open Supply Hub</h3>
              <p>
                Open Supply Hub is a non-profit organisation powering the transition to safe and sustainable production through the world’s most complete, open, and accessible global supply chain map. OS Hub maps production locations and their connections to supply chain actors, helping enable greater transparency, collaboration, and accountability across global supply chains.
              </p>
              <a
                href="https://info.opensupplyhub.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-link"
              >
                Visit OS Hub <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          <div className="contact-card">
            <Mail className="contact-icon" />
            <div className="contact-info">
              <h3>Contact Us</h3>
              <p>
                Please feel free to reach out to us at{" "}
                <a href="mailto:heatwatchindia@gmail.com" className="email-link">
                  heatwatchindia@gmail.com
                </a>
              </p>
            </div>
          </div>
        </motion.section>

        {/* Footer */}
        <footer className="about-footer">
          <p>© {new Date().getFullYear()} HeatWatch Foundation. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default AboutUs;
